import { Component, OnInit } from '@angular/core';
import { CommonModule } from '@angular/common';
import { ReactiveFormsModule, FormBuilder, FormGroup, Validators, AbstractControl, ValidationErrors } from '@angular/forms';
import { Router } from '@angular/router';
import { ImageUrlPipe } from 'app/modules/admin/pipe/image-url.pipe';
import { UserService } from 'app/services/user/user.service';

@Component({
  selector: 'app-add-user',
  standalone: true,
  imports: [CommonModule, ReactiveFormsModule,ImageUrlPipe],
  templateUrl: './add-user.component.html',
  styleUrls: ['./add-user.component.scss']
})
export class AddUserComponent implements OnInit {
  userForm: FormGroup;
  isLoading: boolean = false;
  isSubmitting: boolean = false;
  selectedFileName: string = '';
  imagePreview: string | null = null;
  selectedFile: File | null = null;

  constructor(
    private fb: FormBuilder,
    private userService: UserService,
    private router: Router
  ) {
    this.userForm = this.createForm();
  }

  ngOnInit() {
// نتبع تغييرات كلمة المرور والتأكيد علشان نحدث الفاليديشن
    this.userForm.get('password')?.valueChanges.subscribe(() => {
      this.userForm.get('confirmPassword')?.updateValueAndValidity();
    });

    this.userForm.get('confirmPassword')?.valueChanges.subscribe(() => {
      this.userForm.get('confirmPassword')?.updateValueAndValidity();
    });
  }  

  createForm(): FormGroup {
    return this.fb.group({
      code: [''],
      type: ['', [Validators.required]],
      name: ['', [Validators.required, Validators.minLength(2)]],
      email: ['', [ Validators.email]],
      password: ['', [Validators.required, Validators.minLength(6)]],
      confirmPassword: ['', [Validators.required]],
 phone: ['', [
      Validators.required, 
      Validators.minLength(11),
      Validators.maxLength(11),
      this.egyptPhoneValidator
    ]],
      avatar: [null],
      banned: [false],
      status: [true]
    }, {
      validators: [this.passwordMatchValidator]
    });
  }
// Custom validator للتحقق من رقم الهاتف المصري
egyptPhoneValidator(control: AbstractControl): ValidationErrors | null {
  if (!control.value) {
    return null; // إذا كان فارغ، نترك الـ required validator يشتغل
  }

  const phone = control.value.toString().trim();
  
  // التحقق من أنه رقم
  if (!/^\d+$/.test(phone)) {
    return { phoneNotNumber: 'Phone number must contain only digits' };
  }
  
  // التحقق من أنه يبدأ بـ 01
  if (!phone.startsWith('01')) {
    return { phoneStartWith01: 'Phone number must start with 01' };
  }
  
  // التحقق من الطول
  if (phone.length !== 11) {
    return { phoneLength: 'Phone number must be exactly 11 digits' };
  }
  
  // التحقق من أن الرقم الثالث يكون 0, 1, 2, 5 (شركات المحمول في مصر)
  const thirdDigit = phone[2];
  if (!['0', '1', '2', '5'].includes(thirdDigit)) {
    return { phoneOperator: 'Invalid phone operator' };
  }
  
  return null;
}

   // دالة جديدة للتحقق من إيرور تطابق الباسورد على مستوى الفورم
  showPasswordMismatchError(): boolean {
    return !!this.userForm.errors?.['passwordMismatch'] && 
           this.userForm.get('password')?.touched && 
           this.userForm.get('confirmPassword')?.touched;
  }
  // Custom validator للتحقق من تطابق كلمات المرور
  passwordMatchValidator(control: AbstractControl) {
    const password = control.get('password');
    const confirmPassword = control.get('confirmPassword');

    if (!password || !confirmPassword) {
      return null;
    }

    return password.value === confirmPassword.value ? null : { passwordMismatch: true };
  }

  onFileSelected(event: any) {
    const file = event.target.files[0];
    if (file) {
      // Validate file type
      const validTypes = ['image/jpeg', 'image/png', 'image/webp'];
      if (!validTypes.includes(file.type)) {
        alert('Please select a valid image file (JPG, PNG, WebP)');
        return;
      }

      // Validate file size (2MB max)
      if (file.size > 2 * 1024 * 1024) {
        alert('File size must be less than 2MB');
        return;
      }

      this.selectedFile = file;
      this.selectedFileName = file.name;
      this.userForm.patchValue({ avatar: file });

      // Create preview
      const reader = new FileReader();
      reader.onload = () => {
        this.imagePreview = reader.result as string;
      };
      reader.readAsDataURL(file);
    }
  }

  removeImage() {
    this.selectedFile = null;
    this.selectedFileName = '';
    this.imagePreview = null;
    this.userForm.patchValue({ avatar: '' });
  }

  async onSubmit() {
    if (this.userForm.invalid) {
      this.markAllFieldsAsTouched();
      return;
    }

    this.isSubmitting = true;

    try {
      const formData = this.userForm.value;
      console.log(formData);
      this.userService.createUser(formData).subscribe({
        next: (response) => {
          this.isSubmitting = false;
          alert('✅ User created successfully!');
          this.router.navigateByUrl('/admin/users');
        },
        error: (error) => {
          this.isSubmitting = false;
          console.error('❌ Error creating user:', error);
          
          if (error.status === 409) {
            alert('⚠️ User with this email or phone already exists');
          } else {
            alert('⚠️ Failed to create user. Please try again.');
          }
        }
      });
    } catch (error) {
      this.isSubmitting = false;
      console.error('❌ Error preparing form data:', error);
      alert('⚠️ Error preparing form data. Please check your inputs.');
    }
  }

  prepareFormData(): FormData {
    const formValue = this.userForm.value;
    const formData = new FormData();

    // Append all form fields except password confirmation and avatar
    Object.keys(formValue).forEach(key => {
      if (key !== 'confirmPassword' && key !== 'avatar') {
        formData.append(key, formValue[key]);
      }
    });

    // Append avatar file if selected
    if (this.selectedFile) {
      formData.append('avatar', this.selectedFile);
    }

    return formData;
  }

  markAllFieldsAsTouched() {
    Object.keys(this.userForm.controls).forEach(key => {
      this.userForm.get(key)?.markAsTouched();
    });
  }

  showError(controlName: string): boolean {
    const control = this.userForm.get(controlName);
    return !!(control && control.invalid && (control.dirty || control.touched));
  }

   // في دالة getError أضف معالجة الأخطاء الجديدة
getError(controlName: string): string {
  const control = this.userForm.get(controlName);
  if (control?.errors) {
    if (control.errors['required']) return 'This field is required';
    if (control.errors['email']) return 'Please enter a valid email address';
    if (control.errors['minlength']) return `Minimum length is ${control.errors['minlength'].requiredLength} characters`;
    if (control.errors['passwordMismatch']) return 'Passwords do not match';
    
    // أخطاء الهاتف الجديدة
    if (control.errors['phoneNotNumber']) return control.errors['phoneNotNumber'];
    if (control.errors['phoneStartWith01']) return control.errors['phoneStartWith01'];
    if (control.errors['phoneLength']) return control.errors['phoneLength'];
    if (control.errors['phoneOperator']) return control.errors['phoneOperator'];
  }
  return 'Invalid value';
}


  goBack() {
    this.router.navigateByUrl('/admin/users');
  }

  // دالة مساعدة لعرض حالة الفورم (للت debugging)
  getFormStatus() {
    return {
      valid: this.userForm.valid,
      invalid: this.userForm.invalid,
      errors: this.userForm.errors,
      values: this.userForm.value
    };
  }
}